import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { ConfirmationDialog } from '@/lib/base/overlays';
import { Table } from '@/lib/base/table';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isEditableAction } from '../../lib/proposals/registry/utils';
import {
  mergeActions,
  type MergedAction,
} from '../../lib/proposals/utils/actionMerging';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import { ActionEditContext } from './ActionAccordion';
import { ActionErrorNotice } from './ActionErrorNotice';
import ActionVersionTimeline from './ActionVersionTimeline';
import { EditActionToggle } from './EditActionToggle';
import { MergedCreateActionCard } from './MergedCreateActionCard';
import { mergeActionUpdate } from './multiActionTimeline.utils';
import { useActionEditState } from './useActionEditState';

interface MixedActionExpandedRowProps {
  action: ProposedAction;
  proposal: ProposalDetail;
  onUpdate: (proposal: ProposalDetail) => void;
  isProcessing: boolean;
  /**
   * Total number of columns in the enclosing MixedActionTable (chevron +
   * optional checkbox + status + data columns + optional edit). Used for the
   * expanded row's colSpan so the detail pane spans the full table width.
   */
  totalColumns: number;
  /**
   * When true, the expanded row auto-enters edit mode on mount — used when
   * the user clicks the per-row Edit button in the data row above, so the
   * row expands *and* immediately shows the inline editor.
   */
  autoStartEdit?: boolean;
  /**
   * Dynamic stage association type names (from useAssociationTypes). Passed
   * through to `isEditableAction` so custom stage types are recognised for
   * hidden-entity actions — without this the expanded row would fall back to
   * the hardcoded set and could disagree with the data row about editability.
   */
  stageAssociationTypes?: Set<string>;
}

export const MixedActionExpandedRow = memo(function MixedActionExpandedRow({
  action,
  proposal,
  onUpdate,
  isProcessing,
  totalColumns,
  autoStartEdit = false,
  stageAssociationTypes,
}: MixedActionExpandedRowProps) {
  // Determine if edit button should be shown. `isEditableAction` encapsulates
  // the static per-action gating (registry supportsEditing + gmail_send /
  // risk / hidden-entity-stage-only exclusions); `isProcessing` is layered
  // here as a runtime concern.
  const showEditButton =
    isEditableAction(action, stageAssociationTypes) && !isProcessing;

  const {
    editContextValue,
    activelyEditing,
    isDirty,
    isSaving,
    showDiscardConfirm,
    handleToggleEdit,
    handleSave,
    handleCancel,
    performDiscard,
    handleKeepEditing,
  } = useActionEditState({ actionId: action.id, showEditButton });

  // Auto-enter edit mode when the parent signals this row was opened via the
  // per-row Edit button. Runs at most once per mount — a ref guards the effect
  // so subsequent renders don't re-toggle if the user cancels out of edit.
  const autoStartConsumedRef = useRef(false);
  useEffect(() => {
    if (
      autoStartEdit &&
      showEditButton &&
      !autoStartConsumedRef.current &&
      !activelyEditing
    ) {
      autoStartConsumedRef.current = true;
      handleToggleEdit();
    }
  }, [autoStartEdit, showEditButton, activelyEditing, handleToggleEdit]);

  const actionForTimeline = useMemo(
    () => ({ ...proposal, actions: [action] }),
    [proposal, action]
  );

  // Reconstruct this action's merged group (create + absorbed associations) from
  // the full action list — the table filters the association actions out of the
  // visible rows. When present, the expanded detail renders the SAME
  // MergedCreateActionCard as the accordion/single-action view so the two are
  // visually consistent (entity content + labelled Company/Contact/Stage). Non-
  // create actions fall back to the version timeline.
  const mergedGroup = useMemo<MergedAction | null>(() => {
    for (const item of mergeActions(proposal.actions)) {
      if (
        item.type === 'merged' &&
        item.merged.primaryAction.id === action.id
      ) {
        return item.merged;
      }
    }
    return null;
  }, [proposal.actions, action.id]);

  // A merged-card save (MergedCreateEditForm) commits several actions in one
  // pass — it calls this callback once per changed action (primary payload, each
  // swapped association, each edited stage). Merging every response into the
  // render-snapshot `proposal` would let a later call revert an earlier one, so
  // accumulate through a ref that advances on each call and re-syncs whenever a
  // fresh proposal arrives from the parent.
  const latestProposalRef = useRef(proposal);
  useEffect(() => {
    latestProposalRef.current = proposal;
  }, [proposal]);

  const handleMergedUpdate = useCallback(
    (updatedAction: ProposedAction) => {
      const next = mergeActionUpdate(latestProposalRef.current, updatedAction);
      latestProposalRef.current = next;
      onUpdate(next);
    },
    [onUpdate]
  );

  const handleExpandedUpdate = useCallback(
    (updatedSingleActionProposal: ProposalDetail) => {
      const updatedAction = updatedSingleActionProposal.actions[0];
      if (!updatedAction) {
        return;
      }
      onUpdate(mergeActionUpdate(proposal, updatedAction));
    },
    [proposal, onUpdate]
  );

  return (
    <Table.ExpandedRow
      // Must match the row's React key and the data row's collection id:
      // react-aria throws "Cannot change the id of an item" if a mounted
      // node's id moves, and a save mints a new action.id.
      id={`${getStableActionKey(action)}-expanded`}
      colSpan={totalColumns}
      className='bg-primary'
      // The expanded row hides its default row border ([&>td]:after:hidden), so
      // add an explicit bottom border to separate the detail from the next row.
      cellClassName='border-b border-border-secondary p-5'
    >
      <ActionEditContext.Provider value={editContextValue}>
        {/* Edit button overlays the top-right corner of the content cell
            rather than taking its own row. Absolute positioning keeps the
            action detail flush with the top of the cell while the pencil
            stays accessible. */}
        <div className='relative'>
          {showEditButton && (
            <div className='absolute top-0 right-0 z-10'>
              <EditActionToggle
                isEditing={activelyEditing}
                onToggle={handleToggleEdit}
              />
            </div>
          )}
          <div
            data-component='action-detail'
            data-instance={String(action.id)}
            data-testid={`action-detail-${action.id}`}
            className={`${mergedGroup ? '' : 'max-w-md'} ${showEditButton ? 'pr-10' : ''}`}
          >
            <ActionErrorNotice action={action} />
            {mergedGroup ? (
              <MergedCreateActionCard
                merged={mergedGroup}
                orgId={proposal.org}
                proposalId={proposal.id}
                onUpdate={handleMergedUpdate}
              />
            ) : (
              <ActionVersionTimeline
                action={actionForTimeline}
                onUpdate={handleExpandedUpdate}
                isProcessing={isProcessing}
              />
            )}
          </div>
        </div>
        {activelyEditing && (
          <div className='mt-4 flex justify-end gap-2'>
            <button
              type='button'
              onClick={handleCancel}
              disabled={isSaving}
              className='rounded px-3 py-1.5 text-sm font-medium text-secondary hover:text-primary focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className='rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50'
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
        {/* ConfirmationDialog uses a portal (react-aria ModalOverlay) so its
            position in the React tree doesn't affect DOM layout. Placing it
            inside the Provider ensures it can read context if needed. */}
        <ConfirmationDialog
          isOpen={showDiscardConfirm}
          title='Discard unsaved changes?'
          description='Your changes will be lost.'
          confirmLabel='Discard'
          cancelLabel='Keep editing'
          variant='destructive'
          onConfirm={performDiscard}
          onCancel={handleKeepEditing}
        />
      </ActionEditContext.Provider>
    </Table.ExpandedRow>
  );
});
